import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Account } from 'msal';
import {
    Application,
    Appointment,
    Inspection,
    LicenseListFiltersType,
    PaginationAndLoadingData,
    Violation,
    License,
    WorkflowTransition,
    Workflow,
    LicenseTypeConfiguration,
    AppointmentLicenses,
    FilterCondition,
} from '../modules/application/types';
import { Citation } from '../modules/citation/types';
import { Contact, EventSeries, Person } from '../modules/person/types';
import {
    INSPECTIONS_ORDER_BY_BACKEND,
    LICENSE_ORDER_BY_BACKEND,
    EVENT_ORDER_BY_BACKEND,
    CITATION_ORDER_BY_BACKEND,
    INBOX_ORDER_BY_BACKEND,
    OPERATOR_MAP,
} from '../app.constants';
import { Fee, JournalEntry } from '../modules/accounting/types';
import { LimitAndSkipData } from '../modules/calendar/types';
import { TimeSheet, TimesheetType } from '../modules/timeSheet/type';
import { ViolationsCountFiltersType } from '../modules/application/types';
import { UpdateInboxStatus } from '../modules/inbox/types';
import moment from 'moment';

export const API_CONFIGURATIONS = {
    BASE_URL: process.env.REACT_APP_API_ROOT || `${window.location.origin}/api`,
    MAX_RETRY_COUNT: 2,
    RETRY_INTERVAL: 3000,
    MAX_RETRY_COUNT_FOR_BULK_APPOINTMENT: 3,
};

function withToken(token: string, requestConfig?: AxiosRequestConfig) {
    return Object.assign({}, requestConfig, {
        headers: { ...requestConfig?.headers, Authorization: `Bearer ${token}` },
    });
}

function getFiltersWhereClause(filters?: FilterCondition[]) {
    if (!filters || filters.length === 0) return undefined;

    const criteria: Record<string, any> = [];
    filters.forEach((condition) => {
        const { operator, targetPath, value } = condition;
        if (operator === 'equal') criteria.push({ [targetPath]: value });
        else if (operator === 'like')
            criteria.push({ [targetPath]: { [OPERATOR_MAP[operator]]: value, options: 'i' } });
        else criteria.push({ [targetPath]: { [OPERATOR_MAP[operator]]: value } });
    });
    return criteria.length > 1 ? { and: criteria } : { ...criteria[0] };
}

function getViolationsCountWhereFilter(filters?: ViolationsCountFiltersType) {
    if (!filters) {
        return undefined;
    }

    const criteria: any = {};
    // If more possible filters are identified, they can be added here.
    if (filters?.status && filters?.status.length > 0) {
        criteria['status'] = filters.status;
    }

    return criteria;
}

function getApplicationWhereFilter(filters?: LicenseListFiltersType, searchString?: string) {
    if (!filters && !searchString) {
        return undefined;
    }

    const criteria = [];

    if (filters) {
        if (filters?.licenseStatus.length > 0) {
            const applicationStatuses: string[] = [];

            filters.licenseStatus.forEach((status: string) => {
                applicationStatuses.push(status);
            });

            criteria.push({ status: { inq: [...filters.licenseStatus] } });
        }

        if (filters?.licenseType?.length > 0) {
            const licenseTypeIds: string[] = [];

            filters.licenseType.forEach((type) => {
                licenseTypeIds.push(type.id);
            });

            criteria.push({ 'type.id': { inq: [...licenseTypeIds] } });
        }

        if (filters?.fromDate.length > 0) {
            const fromDate = +moment(filters.fromDate).startOf('day').format('x');

            criteria.push({
                'applications.receivedDate': { gte: fromDate },
            });
        }

        if (filters?.toDate.length > 0) {
            const toDate = +moment(filters.toDate).add(1, 'day').format('x');

            criteria.push({
                'applications.receivedDate': { lt: toDate },
            });
        }
    }

    if (searchString) {
        criteria.push({
            keywords: searchString,
        });
    }

    return criteria.length ? { and: criteria } : undefined;
}

export const Api = {
    getSite: () => {
        return axios.get(`${API_CONFIGURATIONS.BASE_URL}/sites/licensingBackoffice`).then((response) => response.data);
    },
    getApplicationCount: (authToken: string, filters?: LicenseListFiltersType) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/licenses/count?where=`;
        let dataToPost = {};
        dataToPost = {
            ...dataToPost,
        };
        const where = getApplicationWhereFilter(filters);
        dataToPost = { ...dataToPost, ...where };
        url = url + JSON.stringify(dataToPost);
        return axios
            .get(url, withToken(authToken))
            .then((response) => (response?.data.count ? response.data.count : 0));
    },
    getApplicationList: (
        authToken: string,
        searchString?: string,
        filters?: LicenseListFiltersType,
        paginationData?: PaginationAndLoadingData,
    ) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/licenses`;

        let dataToPost = {};

        const orderCol: string =
            paginationData && paginationData.orderCol ? paginationData.orderCol : 'applications[0].receivedDate';

        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';

        dataToPost = {
            order: [`${orderCol} ${orderBy}`],
        };

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.numberOfRows,
                skip: paginationData?.pageNumber * paginationData?.numberOfRows,
            };
        }

        if (!!searchString) {
            dataToPost = {
                ...dataToPost,
                fields: {
                    id: true,
                    type: true,
                    licenseNumber: true,
                    person: true,
                    address: true,
                    additionalData: true,
                },
            };
        }

        const payload = {
            where: getApplicationWhereFilter(filters, searchString),
        };

        dataToPost = { ...dataToPost, ...payload };

        return axios
            .get(url, withToken(authToken, { params: { filter: dataToPost } }))
            .then((response) => response.data);
    },
    getApplication: (authToken: string, licenseId: string) => {
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/licenses/${licenseId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getPersonLicenses: (authToken: string, personId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/licenses?filter=`;
        let dataToPost = {};

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const orderCol: string =
            paginationData && paginationData.orderCol ? LICENSE_ORDER_BY_BACKEND[paginationData.orderCol] : 'issueDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        const filter = {
            order: [`${orderCol} ${orderBy}`],
            where: {
                'person.id': personId,
            },
        };
        dataToPost = { ...dataToPost, ...filter };

        url = url + JSON.stringify(dataToPost);

        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getPersonLicenseCount: (authToken: string, personId: string) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/licenses/count?where=`;
        const where = { 'person.id': personId };
        url = url + JSON.stringify(where);
        return axios
            .get(url, withToken(authToken))
            .then((response) => (response?.data.count ? response.data.count : 0));
    },
    getFilesList: (authToken: string, licenseId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/licenses/${licenseId}/documents?filter=`;

        let dataToPost = {};

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip,
            };
        }

        dataToPost = { ...dataToPost };

        url = url + JSON.stringify(dataToPost);

        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getCitationList: (authToken: string, paginationData?: LimitAndSkipData, filters?: FilterCondition[]) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/citations`;
        let dataToPost = {};

        if (paginationData) {
            dataToPost = {
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const orderCol: string =
            paginationData && paginationData.orderCol
                ? CITATION_ORDER_BY_BACKEND[paginationData.orderCol]
                : 'citationDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        const filter = {
            ...dataToPost,
            order: [`${orderCol} ${orderBy}`],
            where: getFiltersWhereClause(filters),
        };

        return axios.get(url, withToken(authToken, { params: { filter } })).then((response) => response.data);
    },
    getCitationCount: (authToken: string, filters?: FilterCondition[]) => {
        const url = API_CONFIGURATIONS.BASE_URL + `/citations/count`;
        const where = getFiltersWhereClause(filters);
        return axios
            .get(url, withToken(authToken, { params: { where } }))
            .then((response) => (response.data && response.data.count ? response.data.count : 0));
    },
    getCitationDetail: (authToken: string, id: string) => {
        const url = API_CONFIGURATIONS.BASE_URL + `/citations/${id}`;
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getInboxCount: (authToken: string, filters?: FilterCondition[]) => {
        const url = API_CONFIGURATIONS.BASE_URL + `/applications/count`;

        const submittedApplicationsFilter = [{ status: { neq: 'Started' } }, { status: { neq: 'Completed' } }];
        const filtersWhereClause = getFiltersWhereClause(filters);
        const where = {
            ...filtersWhereClause,
            and: (filtersWhereClause?.and ?? []).concat(submittedApplicationsFilter),
            type: {
                neq: null,
            },
        };

        return axios
            .get(url, withToken(authToken, { params: { where } }))
            .then((response) => (response.data && response.data.count ? response.data.count : 0));
    },
    getInboxList: (authToken: string, paginationData?: LimitAndSkipData, filters?: FilterCondition[]) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/applications`;
        let requestParams = {};

        if (paginationData) {
            requestParams = {
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const orderCol: string =
            paginationData && paginationData.orderCol
                ? INBOX_ORDER_BY_BACKEND[paginationData.orderCol]
                : 'submissionDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';

        const submittedApplicationsFilter = [{ status: { neq: 'Started' } }, { status: { neq: 'Completed' } }];
        const filtersWhereClause = getFiltersWhereClause(filters);

        const filter = {
            ...requestParams,
            order: [`${orderCol} ${orderBy}`],
            where: {
                ...filtersWhereClause,
                and: (filtersWhereClause?.and ?? []).concat(submittedApplicationsFilter),
                type: {
                    neq: null,
                },
            },
        };

        return axios.get(url, withToken(authToken, { params: { filter } })).then((response) => response.data);
    },

    updateInboxStatus: (authToken: string, id: string, data: UpdateInboxStatus) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/applications/${id}`;
        return axios.patch(url, data, withToken(authToken)).then((response) => response.status);
    },

    deleteFileDocument: (authToken: string, licenseId: string, fileId: string) => {
        return axios
            .delete(`${API_CONFIGURATIONS.BASE_URL}/licenses/${licenseId}/documents/${fileId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getLicenseAccounting: (authToken: string, id: string, paginationData?: LimitAndSkipData) => {
        let filter: Record<string, unknown> = {
            order: ['journalEntry.date DESC'],
        };

        if (paginationData) {
            filter = {
                ...filter,
                limit: paginationData?.limit,
                skip: (paginationData?.skip ?? 0) * (paginationData?.limit ?? 0),
            };
        }

        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/licenses/${id}/accounting`,
                withToken(authToken, { params: { filter } }),
            )
            .then((response) => response.data);
    },
    getAuditHistoryList: (authToken: string, id: string, filterOn: string, paginationData?: any) => {
        let where: any;
        if (filterOn === 'Files') {
            where = { entityType: 'License' };
        } else if (['License', 'Inspection', 'Violation', 'Citation'].includes(filterOn)) {
            //TODO: This will need to be updated once editing/deleting of comments is supported, along with the response structure
            where = {
                or: [
                    { entityType: filterOn },
                    { and: [{ entityType: 'Comment' }, { 'details.newData.reference.type': filterOn }] },
                ],
            };
        } else {
            where = { entityType: filterOn };
        }

        const limitAndSkip = paginationData
            ? {
                  limit: paginationData.limit,
                  skip: paginationData.skip * paginationData.limit,
              }
            : {};

        const ENTITY_TYPES = [
            'License',
            'Inspection',
            'Violation',
            'Citation',
            'JournalEntry',
            'Comment',
            'MergeQueueItem',
        ];
        const filter =
            filterOn !== 'All'
                ? { order: 'timestamp DESC', where }
                : { order: 'timestamp DESC', where: { entityType: { inq: ENTITY_TYPES } } };

        const url =
            `${API_CONFIGURATIONS.BASE_URL}/licenses/${id}/auditEvents?filter=` +
            JSON.stringify({ ...limitAndSkip, ...filter });

        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getPersonHistoryList: (authToken: string, id: string, filterOn: string) => {
        const where = {
            entityType: `${
                filterOn === 'Contacts' || filterOn === 'Details' || filterOn === 'Files' ? 'Person' : filterOn
            }`,
        };
        const filter = `?filter=${
            filterOn !== 'All'
                ? JSON.stringify({ order: 'timestamp DESC', where })
                : JSON.stringify({ order: 'timestamp DESC' })
        }`;
        const url = `${API_CONFIGURATIONS.BASE_URL}/persons/${id}/auditEvents` + filter;
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getFileToken: (authToken: string, applicationId: string, fileId: string) => {
        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/licenses/${applicationId}/documents/${fileId}/token`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },
    downloadFileDocument: (fileId: string, fileToken: string, fileName: string) => {
        const link = document.createElement('a');
        link.href = `${API_CONFIGURATIONS.BASE_URL}/files/${fileId}?token=${fileToken}`;
        link.download = fileName;
        link.target = '_blank';
        link.click();
    },
    uploadFile: (authToken: string, data: FormData, licenseId: string, options: any, url: string) => {
        return axios.post(url, data, withToken(authToken, options)).then((response) => response.data);
    },
    updateCitation: (authToken: string, data: Citation, citationId: string) => {
        return axios
            .patch(API_CONFIGURATIONS.BASE_URL + `/citations/${citationId}`, data, withToken(authToken))
            .then((response) => response.data);
    },
    voidInvoice: async (authToken: string, invoiceId: string) => {
        return axios.post(
            `${API_CONFIGURATIONS.BASE_URL}/journalEntries/${invoiceId}/void`,
            undefined,
            withToken(authToken),
        );
    },
    updateLicense: (authToken: string, data: Application, licenseId: string) => {
        return axios
            .patch(API_CONFIGURATIONS.BASE_URL + `/licenses/${licenseId}`, data, withToken(authToken))
            .then((response) => response.data);
    },
    exportLicenses: (authToken: string, filters: LicenseListFiltersType) => {
        let url = API_CONFIGURATIONS.BASE_URL + '/licenses';
        const orderCol: string =
            filters && filters.orderCol ? LICENSE_ORDER_BY_BACKEND[filters.orderCol] : 'applications.receivedDate';
        const orderBy: string = filters && filters.orderBy ? filters.orderBy : 'DESC';
        const payload: any = {
            order: [`${orderCol} ${orderBy}`],
            where: {
                and: [],
            },
            fields: {
                id: true,
                licenseNumber: true,
                type: true,
                status: true,
                issueDate: true,
                expirationDate: true,
                person: true,
                specialties: true,
                applications: true,
            },
        };
        if (filters.licenseStatus && filters.licenseStatus.length > 0) {
            payload.where.and.push({
                status: { inq: [...filters.licenseStatus] },
            });
        }
        if (filters.licenseType && filters.licenseType.length > 0) {
            const ids: string[] = [];
            filters.licenseType.forEach((type) => {
                ids.push(type.id);
            });
            payload.where.and.push({
                'type.id': { inq: [...ids] },
            });
        }
        if (filters.fromDate.length > 0 && filters.toDate.length > 0) {
            const fromDate = new Date(filters.fromDate);
            const toDate = new Date(filters.toDate);
            fromDate.setHours(0, 0, 0, 0);
            toDate.setHours(24, 0, 0, 0);
            payload.where.and.push({
                'applications.receivedDate': { between: [fromDate.getTime(), toDate.getTime()] },
            });
        }
        url = url + '?filter=' + JSON.stringify(payload);
        return axios
            .get(
                url,
                withToken(authToken, {
                    headers: { accept: 'text/csv' },
                }),
            )
            .then((response) => response.data);
    },
    getLicenseTypes: (authToken: string): Promise<LicenseTypeConfiguration[]> => {
        const payload = {
            order: ['name ASC'],
        };
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/licenseTypes?filter=` + JSON.stringify(payload), withToken(authToken))
            .then((response) => response.data);
    },
    getLicenseType: (authToken: string, licenseTypeId: string | undefined) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/licenseTypes/${licenseTypeId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getForm: (authToken: string, formId: string | undefined) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/forms/${formId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getWorkflows: async (authToken: string, idList: string[]): Promise<Workflow[]> => {
        const filter = {
            where: {
                id: {
                    inq: idList,
                },
            },
        };

        const response: AxiosResponse<Workflow[]> = await axios.get(
            `${API_CONFIGURATIONS.BASE_URL}/workflows`,
            withToken(authToken, { params: { filter } }),
        );

        return response.data;
    },
    getWorkflowById: async (authToken: string, workflowId: string): Promise<Workflow> => {
        const response: AxiosResponse<Workflow> = await axios.get(
            `${API_CONFIGURATIONS.BASE_URL}/workflows/${workflowId}`,
            withToken(authToken),
        );

        return response.data;
    },
    getWorkflowTransitions: async (
        authToken: string,
        workflowId: string,
        state: string,
    ): Promise<WorkflowTransition[] | undefined> => {
        let workflow: Workflow;

        try {
            const response: AxiosResponse<Workflow> = await axios.get(
                `${API_CONFIGURATIONS.BASE_URL}/workflows/${workflowId}`,
                withToken(authToken),
            );

            workflow = response.data;
        } catch (err) {
            if (err.response?.status === 404) {
                // Workflow doesn't exist.
                return undefined;
            }

            throw err;
        }

        const curState = workflow.states.find((item) => item.name === state);

        if (!curState) {
            // Current state doesn't match workflow, return all workflow states.
            return workflow.states.map((item) => ({ nextState: item.name }));
        }

        return curState.transitions ?? [];
    },
    getInspections: (authToken: string, licenseId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/inspections?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const orderCol: string = paginationData && paginationData.orderCol ? paginationData.orderCol : 'inspectionDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        const filter = {
            order: [`${orderCol} ${orderBy}`],
            where: {
                'license.id': `${licenseId}`,
            },
        };
        dataToPost = { ...dataToPost, ...filter };

        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getInspectionCount: (authToken: string, where: { [key: string]: string }) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/inspections/count?where=`;
        url = url + JSON.stringify(where);
        return axios
            .get(url, withToken(authToken))
            .then((response) => (response?.data.count ? response.data.count : 0));
    },

    getAppointmentInspection: (authToken: string, eventId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/inspections?filter=`;
        let requestParams;
        if (paginationData) {
            requestParams = {
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }
        const orderCol: string =
            paginationData && paginationData.orderCol
                ? INSPECTIONS_ORDER_BY_BACKEND[paginationData.orderCol]
                : 'inspectionDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        const filter = {
            order: [`${orderCol} ${orderBy}`],
            where: {
                'event.id': `${eventId}`,
            },
        };
        requestParams = { ...requestParams, ...filter };

        url = url + JSON.stringify(requestParams);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getCommentList: (authToken: string, licenseId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/licenses/${licenseId}/comments?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
                order: ['timestamp DESC'],
            };
        }
        dataToPost = { ...dataToPost };
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    getViolations: (authToken: string, licenseId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/violations?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const columnSortOrder: string[] =
            paginationData && paginationData.orderCol ? [paginationData.orderCol] : ['inspection.inspectionDate DESC'];
        const filter = {
            order: columnSortOrder,
            where: {
                'license.id': `${licenseId}`,
            },
        };
        dataToPost = { ...dataToPost, ...filter };

        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getViolationsCount: (authToken: string, licenseId: string, filters?: ViolationsCountFiltersType) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/violations/count?where=`;
        let where: any = {
            'license.id': `${licenseId}`,
        };
        const criteria = getViolationsCountWhereFilter(filters);
        where = { ...where, ...criteria };
        url = url + JSON.stringify(where);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getCategories: (authToken: string, licenseTypeId: string) => {
        const filter = {
            order: ['name ASC'],
            where: {
                'licenseTypes.id': `${licenseTypeId}`,
            },
        };
        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/violationCategories?filter=${JSON.stringify(filter)}`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    addViolation: (authToken: string, data: any) => {
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/violations`, data, withToken(authToken))
            .then((response) => response.data);
    },
    addInspection: (authToken: string, data: any) => {
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/inspections`, data, withToken(authToken))
            .then((response) => response.data);
    },
    addComment: (authToken: string, data: any, licenseId: string) => {
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/licenses/${licenseId}/comments`, data, withToken(authToken))
            .then((response) => response.data);
    },
    deleteInspection: (authToken: string, inspectionId: string) => {
        return axios
            .delete(`${API_CONFIGURATIONS.BASE_URL}/inspections/${inspectionId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getUser: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/userProfiles?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },

    getPerson: (authToken: string, personId: string) => {
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/persons/${personId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getPersonType: (authToken: string, personTypeId: string) => {
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/personTypes/${personTypeId}`, withToken(authToken))
            .then((response) => response.data);
    },

    getPersonContacts: (authToken: string, personId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/contacts?filter=`;
        let dataToPost = {};

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }
        dataToPost = { ...dataToPost };

        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getPersonAccounting: (authToken: string, id: string, paginationData?: LimitAndSkipData) => {
        let filter: Record<string, unknown> = {
            order: ['journalEntry.date DESC'],
        };

        if (paginationData) {
            filter = {
                ...filter,
                limit: paginationData?.limit,
                skip: (paginationData?.skip ?? 0) * (paginationData?.limit ?? 0),
            };
        }

        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/persons/${id}/accounting`,
                withToken(authToken, { params: { filter } }),
            )
            .then((response) => response.data);
    },

    updatePerson: (authToken: string, data: Person, personId: string) => {
        return axios.patch(API_CONFIGURATIONS.BASE_URL + `/persons/${personId}`, data, withToken(authToken));
    },

    getInspectionType: (authToken: string, licenseTypeId: string | undefined) => {
        const filter = {
            order: ['name ASC'],
            where: {
                'licenseTypes.id': licenseTypeId,
            },
        };
        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/inspectionTypes?filter=${JSON.stringify(filter)}`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    deleteViolation: (authToken: string, violationId: string) => {
        return axios
            .delete(`${API_CONFIGURATIONS.BASE_URL}/violations/${violationId}`, withToken(authToken))
            .then((response) => response.data);
    },

    getInvoiceTypes: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/invoiceTypes?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },
    getFeeTypes: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/feeTypes?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },
    generateFee: async (authToken: string, feeTypeId: string, data?: unknown) => {
        const response: AxiosResponse<Fee> = await axios.post(
            `${API_CONFIGURATIONS.BASE_URL}/feeTypes/${feeTypeId}/generateFee`,
            data,
            withToken(authToken),
        );

        return response.data;
    },
    getJournalEntries: (authToken: string, params?: Record<string, unknown>) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/journalEntries`, withToken(authToken, { params }))
            .then((response) => response.data);
    },
    addJournalEntry: (authToken: string, journalEntry: JournalEntry) => {
        return axios.post(`${API_CONFIGURATIONS.BASE_URL}/journalEntries`, journalEntry, withToken(authToken));
    },
    addLineItems: (authToken: string, invoiceId: string, lineItems: []) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/journalEntries/${invoiceId}/lineItems`,
                lineItems,
                withToken(authToken),
            )
            .then((response) => response.data);
    },
    getInspectionDetail: (authToken: string, inspectionId: string) => {
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/inspections/${inspectionId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getChoices: (url: string, authToken: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}${url}`, withToken(authToken))
            .then((response) => response.data);
    },

    updateInspection: (authToken: string, data: Inspection, inspectionId: string) => {
        return axios
            .patch(API_CONFIGURATIONS.BASE_URL + `/inspections/${inspectionId}`, data, withToken(authToken))
            .then((response) => response.data);
    },

    addPersonContact: (authToken: string, contact: Contact, personId: string) => {
        return axios
            .post(API_CONFIGURATIONS.BASE_URL + `/persons/${personId}/contacts`, contact, withToken(authToken))
            .then((response) => response.data);
    },

    updatePersonContact: (authToken: string, contact: Contact, personId: string, contactId: string) => {
        return axios
            .patch(
                API_CONFIGURATIONS.BASE_URL + `/persons/${personId}/contacts/${contactId}`,
                contact,
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    getAppointmentList: (authToken: string, personId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/events?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const orderCol: string =
            paginationData && paginationData.orderCol
                ? EVENT_ORDER_BY_BACKEND[paginationData.orderCol]
                : 'startDateTime';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        const filter = {
            order: [`${orderCol} ${orderBy}`],
            where: {
                'persons.id': personId,
            },
        };
        dataToPost = { ...dataToPost, ...filter };

        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    getAppointmentCount: (authToken: string, personId: string) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/events/count?where=`;
        const where = { 'persons.id': personId };
        url = url + JSON.stringify(where);
        return axios
            .get(url, withToken(authToken))
            .then((response) => (response?.data.count ? response.data.count : 0));
    },

    getAppointmentTypeList: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/eventTypes?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },
    getLicenseListForAppointments: (authToken: string, licenseId: string) => {
        const filter = {
            order: ['issueDate DESC'],
            where: {
                'person.id': licenseId,
            },
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/licenses?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },

    getPersonList: (authToken: string, persons: string[]) => {
        const filter = {
            where: { id: { inq: persons } },
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/persons?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },
    addAppointment: (authToken: string, data: any) => {
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/events`, data, withToken(authToken))
            .then((response) => response.data);
    },
    getViolationDetail: (authToken: string, violationId: string) => {
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/violations/${violationId}`, withToken(authToken))
            .then((response) => response.data);
    },
    updateViolation: (authToken: string, data: Violation, violationId: string) => {
        return axios
            .patch(API_CONFIGURATIONS.BASE_URL + `/violations/${violationId}`, data, withToken(authToken))
            .then((response) => response.data);
    },
    getInspectionCommentList: (authToken: string, inspectionId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/inspections/${inspectionId}/comments?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
                order: ['timestamp DESC'],
            };
        }
        dataToPost = { ...dataToPost };
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    addInspectionComment: (authToken: string, data: string, inspectionId: string) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/inspections/${inspectionId}/comments`,
                { text: data },
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    addLicense: (authToken: string, license: License) => {
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/licenses`, license, withToken(authToken))
            .then((response) => response.data);
    },
    addPerson: (authToken: string, person: Person) => {
        return axios
            .post(API_CONFIGURATIONS.BASE_URL + `/persons`, person, withToken(authToken))
            .then((response) => response.data);
    },
    getAppointmentDetail: (authToken: string, appointmentId: string) => {
        return axios
            .get(API_CONFIGURATIONS.BASE_URL + `/events/${appointmentId}`, withToken(authToken))
            .then((response) => response.data);
    },

    updateAppointment: (authToken: string, data: Appointment, appointmentId: string) => {
        return axios
            .patch(API_CONFIGURATIONS.BASE_URL + `/events/${appointmentId}`, data, withToken(authToken))
            .then((response) => response.data);
    },
    getViolationCommentList: (authToken: string, violationId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/violations/${violationId}/comments?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
                order: ['timestamp DESC'],
            };
        }
        dataToPost = { ...dataToPost };
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    addViolationComment: (authToken: string, data: string, violationId: string) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/violations/${violationId}/comments`,
                { text: data },
                withToken(authToken),
            )
            .then((response) => response.data);
    },
    deleteAppointment: (authToken: string, appointmentId: string) => {
        return axios
            .delete(`${API_CONFIGURATIONS.BASE_URL}/events/${appointmentId}`, withToken(authToken))
            .then((response) => response.data);
    },

    deleteContact: (authToken: string, contactId: string, personId: string) => {
        return axios.delete(
            `${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/contacts/${contactId}`,
            withToken(authToken),
        );
    },
    getSpecificLicenses: async (authToken: string, licenseIds: any[] | undefined) => {
        let allLicenses: License[] = [];
        const limit = 50;
        if (licenseIds) {
            while (licenseIds.length > 0) {
                const filter = {
                    where: {
                        id: {
                            inq: licenseIds.splice(0, limit),
                        },
                    },
                };
                const licenses = await axios
                    .get(
                        API_CONFIGURATIONS.BASE_URL + `/licenses?filter=${JSON.stringify(filter)}`,
                        withToken(authToken),
                    )
                    .then((response) => response.data);

                allLicenses = allLicenses.concat(licenses);
            }
        }
        return allLicenses;
    },
    getViolationCategory: (authToken: string, categoryId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/violationCategories/${categoryId}`, withToken(authToken))
            .then((response) => {
                return response.data;
            });
    },
    getAppointmentCommentList: (authToken: string, appointmentId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/events/${appointmentId}/comments?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
                order: ['timestamp DESC'],
            };
        }
        dataToPost = { ...dataToPost };
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getCitationTypeById: (authToken: string, citationTypeId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/citationTypes/${citationTypeId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getInspectionTypeById: (authToken: string, inspectionTypeId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/inspectionTypes/${inspectionTypeId}`, withToken(authToken))
            .then((response) => response.data);
    },

    addCitationComment: (authToken: string, data: string, citationId: string) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/citations/${citationId}/comments`,
                { text: data },
                withToken(authToken),
            )
            .then((response) => response.data);
    },
    getCitationCommentList: (authToken: string, citationId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/citations/${citationId}/comments?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
                order: ['timestamp DESC'],
            };
        }
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    getPersonCommentsList: (authToken: string, personId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/comments?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
                order: ['timestamp DESC'],
            };
        }
        dataToPost = { ...dataToPost };
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    addPersonComment: (authToken: string, comment: string, personId: string) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/comments`,
                { text: comment },
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    getMergeTemplates: (authToken: string, payload: any) => {
        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/mergeTemplates?filter=${JSON.stringify(payload)}`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    mergeDocuments: (
        authToken: string,
        templateId: string,
        fileName: string,
        printHistoryId: string,
        isInspectorPack?: boolean,
        filters?: FilterCondition[],
        records?: string[],
        outputFormat?: string,
    ) => {
        let url = '';
        const mergeDocumentDownloadURL = `${API_CONFIGURATIONS.BASE_URL}/mergeTemplates/${templateId}/merge`;
        const documentDownloadURL = `${API_CONFIGURATIONS.BASE_URL}/mergeTemplates/${templateId}/queue/merge`;
        const historyDocumentDownloadURL = `${API_CONFIGURATIONS.BASE_URL}/mergeTemplates/${templateId}/history/${printHistoryId}/merge`;

        let payload: any = { where: {} };

        if (isInspectorPack) {
            if (filters !== undefined) {
                payload.where = getFiltersWhereClause(filters);
                const filter = JSON.stringify(payload);
                url = `${API_CONFIGURATIONS.BASE_URL}/events/report?filter=${filter}&mergeTemplateId=${templateId}`;
            }
        } else if (printHistoryId) {
            url = historyDocumentDownloadURL;
        } else if (records?.length) {
            url = mergeDocumentDownloadURL;
            payload = { mergeIds: records, fileName, outputFormat: outputFormat ? outputFormat : 'DOCX' };
        } else {
            url = documentDownloadURL;
        }

        return axios.post(url, payload, withToken(authToken, { responseType: 'arraybuffer' })).then((response: any) => {
            if (!response.data.byteLength) {
                return { response: 'There are no documents ready for download.', status: 204 };
            } else {
                // Create blob link to download
                const fileBuffer = new Blob([response.data], { type: 'application/docx' });
                const url = window.URL.createObjectURL(fileBuffer);
                const link = document.createElement('a');
                link.href = url;

                link.setAttribute('download', `${fileName}.docx`);

                // Append to html link element page
                document.body.appendChild(link);

                // Start download
                link.click();

                // Clean up and remove the link
                link?.parentNode?.removeChild(link);
                return { response: 'File downloaded successfully.', status: 200 };
            }
        });
    },
    addAppointmentComment: (authToken: string, data: any, appointmentId: string) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/events/${appointmentId}/comments`,
                { text: data },
                withToken(authToken),
            )
            .then((response) => {
                return response.data;
            });
    },

    getEventType: (authToken: string, eventTypeId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/eventTypes/${eventTypeId}`, withToken(authToken))
            .then((response) => response.data);
    },

    getPersonTypes: (authToken: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/personTypes`, withToken(authToken))
            .then((response) => response.data);
    },
    getContactTypes: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
            fields: {
                id: true,
                name: true,
            },
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/contactTypes?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },

    getEventList: (authToken: string, paginationData?: LimitAndSkipData, filters?: FilterCondition[]) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/events`;
        let dataToPost = {};

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const orderCol: string =
            paginationData && paginationData.orderCol
                ? EVENT_ORDER_BY_BACKEND[paginationData.orderCol]
                : 'startDateTime';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'ASC';
        const filter = {
            ...dataToPost,
            order: [`${orderCol} ${orderBy}`],
            where: getFiltersWhereClause(filters),
        };

        return axios
            .get(url, withToken(authToken, { params: { filter, tzoffset: moment().format('Z') } }))
            .then((response) => response.data);
    },
    getEventCount: (authToken: string, filters?: FilterCondition[]) => {
        const url = API_CONFIGURATIONS.BASE_URL + `/events/count`;
        const where = getFiltersWhereClause(filters);
        const tzoffset = moment().format('Z');

        return axios
            .get(url, withToken(authToken, { params: { where, tzoffset } }))
            .then((response) => (response.data && response.data.count ? response.data.count : 0));
    },

    getViolationsAssociatedWithInspection: (
        authToken: string,
        inspectionId: string,
        paginationData?: LimitAndSkipData,
    ) => {
        let dataToPost = {};
        let url = `${API_CONFIGURATIONS.BASE_URL}/violations?filter=`;

        const columnSortOrder: string =
            paginationData && paginationData.orderCol ? paginationData.orderCol : 'dueDate asc';

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }
        const filter = {
            order: [`${columnSortOrder}`],
            where: {
                'inspection.id': `${inspectionId}`,
            },
        };
        dataToPost = { ...dataToPost, ...filter };

        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    getTimeSheetList: (authToken: string, paginationData?: LimitAndSkipData, filters?: FilterCondition[]) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/timesheets`;
        let dataToPost = {};

        const orderCol: string = paginationData && paginationData.orderCol ? paginationData.orderCol : 'startDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const filter = {
            ...dataToPost,
            order: [`${orderCol} ${orderBy}`],
            where: getFiltersWhereClause(filters),
        };

        return axios.get(url, withToken(authToken, { params: { filter } })).then((response) => response.data);
    },

    getTimeSheetCount: (authToken: string, filters?: FilterCondition[]) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/timesheets/count?where=`;
        const where = getFiltersWhereClause(filters);
        let dataToPost = {};

        dataToPost = { ...dataToPost, ...where };

        url = url + JSON.stringify(dataToPost);
        return axios
            .get(url, withToken(authToken))
            .then((response) => (response.data && response.data.count ? response.data.count : 0));
    },

    getPersonListSearched: (authToken: string, searchString?: string, paginationData?: PaginationAndLoadingData) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/persons`;
        let dataToPost = {};

        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.numberOfRows,
                skip: paginationData?.pageNumber * paginationData?.numberOfRows,
            };
        }
        const where: any = {
            or: [
                { 'address.line1': { like: searchString, options: 'i' } },
                { name: { like: searchString, options: 'i' } },
            ],
        };

        searchString?.split(' ').forEach((keyword: string) => {
            where.or.push({ 'name.firstName': { like: keyword, options: 'i' } });
            where.or.push({ 'name.lastName': { like: keyword, options: 'i' } });
        });
        if (!!searchString) {
            dataToPost = {
                ...dataToPost,
                where,
            };
        }

        return axios.get(url, withToken(authToken, { params: { filter: dataToPost } })).then((response) => {
            const persons = response.data;
            const exactMatched = persons.filter((person: Person) => {
                if (typeof person.name === 'object') {
                    return (
                        person.name.firstName.toLowerCase() + ' ' + person.name.lastName.toLowerCase() ===
                        searchString?.toLowerCase()
                    );
                }
                return false;
            });
            if (exactMatched.length > 0) {
                return exactMatched;
            }
            return persons;
        });
    },
    getTimeSheetTypes: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };

        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/timesheetTypes?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },

    getTimeSheetCategories: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };

        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/timeCategories?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },

    setTimeSheetForSelectedDate: (authToken: string, date: string, user: Account, timeSheetType: TimesheetType[]) => {
        const data = {
            type: {
                id: timeSheetType[0].id,
                name: timeSheetType[0].name,
            },
            user: {
                id: user.accountIdentifier,
                name: user.name,
            },
        };
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/timesheets?timeCardDate=${date}`, data, withToken(authToken))
            .then((response) => response.data);
    },

    getAppointmentsBySelectedDate: (authToken: string, date: string, userId: string) => {
        const filter = {
            where: {
                'participants.id': `${userId}`,
                startDateTime: {
                    like: `^${date}`,
                    options: 'i',
                },
            },
        };
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/events?filter=${JSON.stringify(filter)}`, withToken(authToken))
            .then((response) => response.data);
    },

    getTimeSheetExpenseCategories: (authToken: string) => {
        const filter = {
            order: ['name ASC'],
        };

        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/expenseCategories?filter=${JSON.stringify(filter)}`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    getTimeSheetCommentsList: (authToken: string, timeSheetId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/timesheets/${timeSheetId}/comments`, withToken(authToken))
            .then((response) => response.data);
    },

    addTimeSheetComment: (authToken: string, comment: string, timeSheetId: string) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/timesheets/${timeSheetId}/comments`,
                { text: comment },
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    getTimeSheetDetails: (authToken: string, timeSheetId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/timesheets/${timeSheetId}`, withToken(authToken))
            .then((response) => response.data);
    },

    saveTimeSheet: (authToken: string, timeSheet: TimeSheet, timeSheetId: string) => {
        return axios
            .patch(`${API_CONFIGURATIONS.BASE_URL}/timesheets/${timeSheetId}`, timeSheet, withToken(authToken))
            .then((response) => response.data);
    },

    getPrintDocumentHistoryList: (authToken: string, templateId: string, paginationData?: LimitAndSkipData) => {
        let url = API_CONFIGURATIONS.BASE_URL + `/mergeTemplates/${templateId}/history?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }

        const payload: any = {
            order: ['timestamp DESC'],
            where: {
                'mergeTemplate.id': templateId,
                parentId: null,
            },
        };

        dataToPost = { ...dataToPost, ...payload };
        url = url + JSON.stringify(dataToPost);

        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    deletePersonFileDocument: (authToken: string, personId: string, fileId: string) => {
        return axios
            .delete(`${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/documents/${fileId}`, withToken(authToken))
            .then((response) => response.data);
    },
    getPersonFilesList: (authToken: string, personId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/documents?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                ...dataToPost,
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }
        dataToPost = { ...dataToPost };
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    getPersonFileToken: (authToken: string, personId: string, fileId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/persons/${personId}/documents/${fileId}/token`, withToken(authToken))
            .then((response) => response.data);
    },

    mergeTimesheetDocuments: (authToken: string, templateId: string, fileName: string, timesheetId: string) => {
        const url = `${API_CONFIGURATIONS.BASE_URL}/timesheets/${timesheetId}/reports?mergeTemplateId=${templateId}`;
        return axios.post(url, {}, withToken(authToken, { responseType: 'arraybuffer' })).then((response: any) => {
            if (!response.data.byteLength) {
                return { response: 'There are no documents ready for download.', status: 204 };
            } else {
                // Create blob link to download
                const fileBuffer = new Blob([response.data], { type: 'application/docx' });
                const url = window.URL.createObjectURL(fileBuffer);
                const link = document.createElement('a');
                link.href = url;

                link.setAttribute('download', `${fileName}.docx`);

                // Append to html link element page
                document.body.appendChild(link);

                // Start download
                link.click();

                // Clean up and remove the link
                link?.parentNode?.removeChild(link);
                return { response: 'File downloaded successfully.', status: 200 };
            }
        });
    },

    getCitationsFileList: (authToken: string, citationId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/citations/${citationId}/documents?filter=`;
        let dataToPost = {};
        if (paginationData) {
            dataToPost = {
                limit: paginationData?.limit,
                skip: paginationData?.skip * paginationData?.limit,
            };
        }
        url = url + JSON.stringify(dataToPost);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },

    deleteCitationsFileDocument: (authToken: string, citationId: string, fileId: string) => {
        return axios
            .delete(`${API_CONFIGURATIONS.BASE_URL}/citations/${citationId}/documents/${fileId}`, withToken(authToken))
            .then((response) => response.data);
    },

    getCitationsFileToken: (authToken: string, citationId: string, fileId: string) => {
        return axios
            .get(
                `${API_CONFIGURATIONS.BASE_URL}/citations/${citationId}/documents/${fileId}/token`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },
    getLinkedLicensesForAppointments: (authToken: string, appointmentId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/events/${appointmentId}/licenses`, withToken(authToken))
            .then((response) => response.data);
    },

    getFilteredLicensesForAppointment: async (authToken: string, personIds: string[]) => {
        let allLicenses: License[] = [];

        const limit = 50;

        if (personIds) {
            while (personIds.length > 0) {
                const filter = {
                    where: {
                        'person.id': {
                            inq: personIds.splice(0, limit),
                        },
                    },
                };

                const licenses = await axios

                    .get(
                        `${API_CONFIGURATIONS.BASE_URL}/licenses?filter=${JSON.stringify(filter)}`,

                        withToken(authToken),
                    )

                    .then((response) => response.data);

                allLicenses = allLicenses.concat(licenses);
            }
        }

        return allLicenses;
    },
    addLicenseForAppointment: (authToken: string, appointmentId: string, selectedLicenses: AppointmentLicenses) => {
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/events/${appointmentId}/licenses`,
                selectedLicenses,
                withToken(authToken),
            )
            .then((response) => response.data);
    },
    removeLicenseFromAppointment: (authToken: string, appointmentId: string, licenseId: string) => {
        return axios
            .delete(
                `${API_CONFIGURATIONS.BASE_URL}/events/${appointmentId}/licenses/${licenseId}`,
                withToken(authToken),
            )
            .then((response) => response.data);
    },

    addBulkAppointment: (authToken: string, data: EventSeries) => {
        return axios
            .post(`${API_CONFIGURATIONS.BASE_URL}/eventSeries`, data, withToken(authToken))
            .then((response) => response.data);
    },

    getOpenedViolations: (authToken: string, licenseId: string, paginationData?: LimitAndSkipData) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/violations?filter=`;

        const orderCol: string =
            paginationData && paginationData.orderCol ? paginationData.orderCol : 'inspection.inspectionDate';
        const orderBy: string = paginationData && paginationData.orderBy ? paginationData.orderBy : 'DESC';
        const filter = {
            order: [`${orderCol} ${orderBy}`],
            where: {
                'license.id': `${licenseId}`,
                status: 'Found',
            },
        };
        url = url + JSON.stringify(filter);
        return axios.get(url, withToken(authToken)).then((response) => response.data);
    },
    closeBulkViolations: (authToken: string, data: any) => {
        let url = `${API_CONFIGURATIONS.BASE_URL}/violations?where=`;
        const filter = {
            id: {
                inq: data,
            },
        };
        url = url + JSON.stringify(filter);
        return axios.patch(url, { status: 'Corrected' }, withToken(authToken)).then((response) => response.data);
    },
    getInvoiceType: (authToken: string, invoiceTypeId: string) => {
        return axios
            .get(`${API_CONFIGURATIONS.BASE_URL}/invoiceTypes/${invoiceTypeId}`, withToken(authToken))
            .then((response) => response.data);
    },
    downloadInvoice: (authToken: string, mergeTemplateInvoiceId: string, fileName: string, invoiceId: string) => {
        const payload = {
            mergeIds: [invoiceId],
            fileName,
            outputFormat: 'DOCX',
        };
        return axios
            .post(
                `${API_CONFIGURATIONS.BASE_URL}/mergeTemplates/${mergeTemplateInvoiceId}/merge`,
                payload,
                withToken(authToken, { responseType: 'arraybuffer' }),
            )
            .then((response: any) => {
                if (!response.data.byteLength) {
                    return { response: 'There are no documents ready for download.', status: 204 };
                } else {
                    // Create blob link to download
                    const fileBuffer = new Blob([response.data], { type: 'application/docx' });
                    const url = window.URL.createObjectURL(fileBuffer);
                    const link = document.createElement('a');
                    link.href = url;

                    link.setAttribute('download', `${fileName}.docx`);

                    // Append to html link element page
                    document.body.appendChild(link);

                    // Start download
                    link.click();

                    // Clean up and remove the link
                    link?.parentNode?.removeChild(link);
                    return { response: 'File downloaded successfully.', status: 200 };
                }
            });
    },
    createPayment: (authToken: string, payload: unknown) => {
        return axios.post(`${API_CONFIGURATIONS.BASE_URL}/payments`, payload, withToken(authToken));
    },
};
